import React from "react";
import { Button, CircularProgress, Box, IconButton, Tooltip } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import HistoryIcon from "@mui/icons-material/History";
import PropTypes from "prop-types";

/**
 * A reusable component for action buttons with consistent sizing
 * 
 * @param {Object} props - Component props
 * @param {string} props.type - Button type (upload, download, process, back, reset, history, custom, icon)
 * @param {Function} props.onClick - Function to call when button is clicked
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {boolean} props.isProcessing - Whether processing is in progress
 * @param {boolean} props.isUploading - Whether uploading is in progress
 * @param {string} props.label - Button label (optional)
 * @param {string} props.variant - Button variant (contained, outlined)
 * @param {string} props.color - Button color (primary, secondary)
 * @param {Object} props.sx - Additional styles
 * @param {boolean} props.showIcon - Whether to show the icon (defaults to true)
 * @param {string} props.iconPosition - Icon position ("start" or "end", overrides default position)
 * @param {React.ReactNode} props.customIcon - Custom icon to use
 * @param {boolean} props.isLoading - General loading state
 * @param {string} props.size - Button size (small, medium, large)
 * @param {string} props.status - Status for status-based styling
 * @param {string} props.loadingText - Text to display when loading
 * @param {Object} props.style - Inline style object (legacy prop)
 * @param {boolean} props.fullWidth - Whether the button should take up full width
 * @param {boolean} props.iconButton - Whether to render as an IconButton (no text, just icon)
 * @param {string} props.tooltip - Tooltip text for icon buttons
 */
const CButton = ({
  type,
  onClick,
  disabled = false,
  isProcessing = false,
  isUploading = false,
  isLoading = false,
  label,
  variant = "contained",
  color = "primary",
  sx = {},
  showIcon = true,
  iconPosition,
  customIcon,
  size = "medium",
  status,
  loadingText,
  style,
  fullWidth = false,
  iconButton = false,
  tooltip,
  ...rest
}) => {
  // Determine if the button is in a loading state
  const isInLoadingState = isProcessing || isUploading || isLoading;
  
  // Get button configuration based on type and status
  const getButtonConfig = () => {
    // Default icon position is "end" for all buttons except upload
    const defaultIconPosition = type === "upload" ? "start" : "end";
    
    // Base size configurations to ensure text and icons don't get too small
    const baseIconSizes = {
      small: 18,
      medium: 22,
      large: 26
    };
    
    const baseFontSizes = {
      small: '0.75rem',
      medium: '0.875rem',
      large: '1rem'
    };
    
    // Minimum button dimensions by size
    const minButtonDimensions = {
      small: { minHeight: '32px', minWidth: iconButton ? '32px' : '80px' },
      medium: { minHeight: '36px', minWidth: iconButton ? '36px' : '120px' },
      large: { minHeight: '42px', minWidth: iconButton ? '42px' : '150px' }
    };
    
    // Status-based colors for special styling
    if (status) {
      const statusLower = status.toLowerCase();
      
      if (statusLower === "to be processed") {
        return {
          label: label || "TO BE PROCESSED",
          icon: customIcon || null,
          iconPosition: iconPosition || defaultIconPosition,
          variant: "outlined",
          color: "primary",
          sx: {
            textTransform: 'uppercase',
            fontSize: baseFontSizes[size],
            minWidth: minButtonDimensions[size].minWidth,
            minHeight: minButtonDimensions[size].minHeight,
            px: 2,
            py: 0.5,
            borderRadius: '16px',
            fontWeight: 600,
            letterSpacing: '0.5px',
            cursor: 'pointer',
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            color: '#1976d2',
            border: '1px solid #1976d2',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.12)',
              boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
              transform: 'translateY(-1px)',
              transition: 'all 0.2s ease-in-out',
            },
            '& .MuiSvgIcon-root': {
              fontSize: baseIconSizes[size]
            }
          }
        };
      }
      
      if (statusLower === "completed") {
        return {
          label: label || status,
          icon: customIcon || null,
          iconPosition: iconPosition || defaultIconPosition,
          variant: variant || "outlined",
          color: "success",
          sx: {
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            color: '#2e7d32',
            border: '1px solid #4caf50',
            textTransform: 'uppercase',
            fontSize: baseFontSizes[size],
            minWidth: minButtonDimensions[size].minWidth,
            minHeight: minButtonDimensions[size].minHeight,
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderRadius: '16px',
            '& .MuiSvgIcon-root': {
              fontSize: baseIconSizes[size]
            }
          }
        };
      }
      
      if (statusLower === "processing" || statusLower === "in progress") {
        return {
          label: label || status,
          icon: customIcon || <CircularProgress size={baseIconSizes[size]} color="inherit" />,
          iconPosition: iconPosition || "start",
          variant: variant || "outlined",
          color: "warning",
          sx: {
            backgroundColor: 'rgba(255, 223, 0, 0.1)',
            color: '#daa520',
            border: '1px solid #eec900',
            textTransform: 'uppercase',
            fontSize: baseFontSizes[size],
            minWidth: minButtonDimensions[size].minWidth,
            minHeight: minButtonDimensions[size].minHeight,
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderRadius: '16px',
            '& .MuiSvgIcon-root': {
              fontSize: baseIconSizes[size]
            }
          }
        };
      }
      
      if (statusLower === "failed") {
        return {
          label: label || status,
          icon: customIcon || null,
          iconPosition: iconPosition || defaultIconPosition,
          variant: variant || "outlined",
          color: "error",
          sx: {
            backgroundColor: 'rgba(244, 67, 54, 0.1)',
            color: '#d32f2f',
            border: '1px solid #f44336',
            textTransform: 'uppercase',
            fontSize: baseFontSizes[size],
            minWidth: minButtonDimensions[size].minWidth,
            minHeight: minButtonDimensions[size].minHeight,
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderRadius: '16px',
            '& .MuiSvgIcon-root': {
              fontSize: baseIconSizes[size]
            }
          }
        };
      }
    }
    
    // Common styles for all button types
    const commonSx = {
      fontSize: baseFontSizes[size],
      minWidth: minButtonDimensions[size].minWidth,
      minHeight: minButtonDimensions[size].minHeight,
      '& .MuiSvgIcon-root': {
        fontSize: baseIconSizes[size]
      }
    };
    
    // Standard button types
    switch (type) {
      case "upload":
        return {
          label: isUploading ? (loadingText || "Uploading...") : (label || "Upload"),
          icon: isUploading ? 
            <CircularProgress size={baseIconSizes[size]} color="inherit" /> : 
            (customIcon || <CloudUploadIcon />),
          iconPosition: iconPosition || "start",
          variant: variant || "contained",
          color: color || "primary",
          sx: commonSx
        };
      case "download":
        return {
          label: label || "Download Results",
          icon: customIcon || <FileDownloadIcon />,
          iconPosition: iconPosition || defaultIconPosition,
          variant: variant || "contained",
          color: color || "primary",
          sx: commonSx
        };
      case "downloadSample":
        return {
          label: label || "Download Sample",
          icon: customIcon || <FileDownloadIcon />,
          iconPosition: iconPosition || defaultIconPosition,
          variant: variant || "outlined",
          color: color || "primary",
          sx: commonSx
        };
      case "process":
        return {
          label: isProcessing ? (loadingText || "Processing...") : (label || "Start Processing"),
          icon: isProcessing ? 
            <CircularProgress size={baseIconSizes[size]} color="inherit" /> : 
            (customIcon || <PlayCircleFilledWhiteIcon />),
          iconPosition: iconPosition || defaultIconPosition,
          variant: variant || "contained",
          color: color || "primary",
          sx: commonSx
        };
      case "back":
        return {
          label: label || "Back",
          icon: customIcon || null,
          iconPosition: iconPosition || defaultIconPosition,
          variant: variant || "outlined",
          color: color || "primary",
          sx: commonSx
        };
      case "reset":
        return {
          label: label || "Start New Process",
          icon: customIcon || null,
          iconPosition: iconPosition || defaultIconPosition,
          variant: variant || "outlined",
          color: color || "primary",
          sx: commonSx
        };
      case "history":
        return {
          label: label || "History",
          icon: customIcon || <HistoryIcon />,
          iconPosition: iconPosition || defaultIconPosition,
          variant: variant || "outlined",
          color: color || "primary",
          sx: commonSx
        };
      case "icon":
        return {
          label: label || "",
          icon: customIcon || null,
          iconPosition: "center", // Not applicable for IconButton
          variant: variant || "contained",
          color: color || "primary",
          sx: {
            ...commonSx,
            // For icon buttons, ensure square dimensions
            width: minButtonDimensions[size].minHeight,
            height: minButtonDimensions[size].minHeight,
          }
        };
      case "custom":
      default:
        return {
          label: label || "Action",
          icon: customIcon || null,
          iconPosition: iconPosition || defaultIconPosition,
          variant: variant || "contained",
          color: color || "primary",
          sx: commonSx
        };
    }
  };

  const config = getButtonConfig();
  
  // Use the showIcon prop to determine if icons should be displayed
  const shouldShowIcon = showIcon && (config.icon || isInLoadingState);
  
  const finalIcon = isInLoadingState ? 
    <CircularProgress 
      size={
        size === "small" ? 18 : 
        size === "medium" ? 22 : 
        26
      } 
      color="inherit" 
    /> : 
    config.icon;
  
  const finalStartIcon = shouldShowIcon && !iconButton && config.iconPosition === "start" ? finalIcon : null;
  const finalEndIcon = shouldShowIcon && !iconButton && config.iconPosition === "end" ? finalIcon : null;

  // Merge component styles with any provided custom styles
  const mergedSx = {
    ...config.sx,
    ...sx,
    // Ensure consistent font size and icon size even when container dimensions change
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  // If it should be rendered as an icon button
  if (iconButton || type === "icon") {
    const iconButtonContent = (
      <IconButton
        color={config.color}
        onClick={onClick}
        disabled={disabled || isInLoadingState}
        size={size}
        sx={mergedSx}
        style={style}
        {...rest}
      >
        {finalIcon}
      </IconButton>
    );

    // Wrap in tooltip if tooltip text is provided
    return tooltip ? (
      <Tooltip title={tooltip}>
        {iconButtonContent}
      </Tooltip>
    ) : iconButtonContent;
  }

  // Otherwise, render as a regular button
  const buttonContent = (
    <Button
      variant={config.variant}
      color={config.color}
      onClick={onClick}
      disabled={disabled || isInLoadingState}
      startIcon={finalStartIcon}
      endIcon={finalEndIcon}
      size={size}
      fullWidth={fullWidth}
      sx={mergedSx}
      style={style}
      {...rest}
    >
      {isInLoadingState && loadingText ? loadingText : config.label}
    </Button>
  );

  // Wrap in tooltip if tooltip text is provided
  return tooltip ? (
    <Tooltip title={tooltip}>
      {buttonContent}
    </Tooltip>
  ) : buttonContent;
};

CButton.propTypes = {
  type: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  isProcessing: PropTypes.bool,
  isUploading: PropTypes.bool,
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  variant: PropTypes.string,
  color: PropTypes.string,
  sx: PropTypes.object,
  showIcon: PropTypes.bool,
  iconPosition: PropTypes.oneOf(['start', 'end']),
  customIcon: PropTypes.node,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  status: PropTypes.string,
  loadingText: PropTypes.string,
  style: PropTypes.object,
  fullWidth: PropTypes.bool,
  iconButton: PropTypes.bool,
  tooltip: PropTypes.string
};

export default CButton;